import { _getLogger } from '@doxyme/analytics';
import {
  BasicObservable,
  Specs,
  attachHandler,
} from '@doxyme/extensions-common';
import type Instance from '../../../instance';
import {
  logErrorAndThrowBind,
  type LogErrorAndThrowBound,
} from '../../../utils';
import AppletCapability from '../applet';

const { ConfigApplet } = Specs;

type Constraints = Specs.ConfigApplet.Constraints;
type ConfigObject = Specs.ConfigApplet.ConfigObject;
type Applet = Specs.ConfigApplet.Applet;

export type SaveHandler = {
  (manifestId: string, config: ConfigObject): Promise<void>;
};
export type GetHandler = {
  (manifestId: string): ConfigObject | undefined;
};
export type StateDirtyHandler = {
  (): void;
};
export type StateResetHandler = {
  (): void;
};

const logger = _getLogger('configApplet', {
  component: 'extensions',
  lib: 'bridge',
});
const logErrorAndThrow: LogErrorAndThrowBound = logErrorAndThrowBind(logger);

/**
 * Config Applet exposes the active frame registered to any extension using this capability and attached.
 * As well as methods to assign save/get/stateDirty/stateReset handlers and attach method to give a control to the host app.
 *
 * Intended use is inside the settings where a user can render a config applet frame
 * and then receive the configuration details from the extension, so it can save it to the registry.
 */
export default class ConfigAppletCapability extends AppletCapability<Constraints> {
  static id = ConfigApplet.id;

  protected override readonly hasDynamicHeight = true;

  private readonly activeFrame: BasicObservable<Applet | undefined>;

  private readonly saveHandlers: SaveHandler[] = [];

  private readonly getHandlers: GetHandler[] = [];

  private readonly stateDirtyHandlers: StateDirtyHandler[] = [];

  private readonly stateResetHandlers: StateResetHandler[] = [];

  constructor() {
    super(ConfigApplet);

    this.activeFrame = new BasicObservable<Applet | undefined>(
      this._frames.value[0],
    );
    this._frames.onChange((frames) => (this.activeFrame.value = frames[0]));

    this.handlers.push(
      [ConfigApplet.Actions.Save, this.save.bind(this)],
      [ConfigApplet.Actions.Get, this.get.bind(this)],
      [ConfigApplet.Actions.StateDirty, this.setDirtyState.bind(this)],
      [ConfigApplet.Actions.StateReset, this.resetState.bind(this)],
    );
  }

  /**
   * Override to prevent auto attaching of config frames
   */
  override addInstance(instance: Instance): void {
    super.addInstanceBase(instance);
  }

  /**
   * Get the active frame
   * @description This is an observable that will update whenever the active frame is updated. See {@link BasicObservable} for more information.
   * @returns The active frame if available, as an observable.
   * @example
   * ```ts
   * const activeFrame = configApplet.getActiveFrame();
   * activeFrame.onChange((frame) => {
   *  console.log('Active frame changed', frame);
   * });
   * ```
   */
  getActiveFrame(): BasicObservable<Applet | undefined> {
    return this.activeFrame;
  }

  /**
   * Attach config frame
   * This method is exposed to the host app and responsible for attaching a config frame.
   * Attaches only one frame from the capabilities configuration if multiple defined.
   * Previous active frame should be detached before.
   */
  override attach(manifestId: string): void {
    const instance = this.getInstanceByManifestId(manifestId);
    this.attachOne(undefined, instance);
  }

  /**
   * Assign onSave handler, will be called on "save" action.
   */
  onSave(handler: SaveHandler): () => void {
    return attachHandler<SaveHandler>(this.saveHandlers, handler);
  }

  /**
   * Assign onGet handler, will be called on "get" action.
   */
  onGet(handler: GetHandler): () => void {
    return attachHandler<GetHandler>(this.getHandlers, handler);
  }

  /**
   * Assign onStateDirty handler, will be called on "state-dirty" action.
   */
  onStateDirty(handler: StateDirtyHandler): () => void {
    return attachHandler<StateDirtyHandler>(this.stateDirtyHandlers, handler);
  }

  /**
   * Assign onStateReset handler, will be called on "state-reset" action.
   */
  onStateReset(handler: StateResetHandler): () => void {
    return attachHandler<StateResetHandler>(this.stateResetHandlers, handler);
  }

  /**
   * Detach current active frame if attached.
   */
  detachActiveFrame(): void {
    if (!this.activeFrame.value) {
      return;
    }
    const runtimeId = this.activeFrame.value.runtimeId;
    const runtime = this.getRuntimeBy(runtimeId);
    if (!runtime) {
      logErrorAndThrow(
        '[detachActiveFrame] No runtime found for active frame',
        { applet: { runtimeId: this.activeFrame.value.runtimeId } },
      );
    }
    this.detach(runtime.config.name, runtime.instance);
  }

  /**
   * "Save" action handler, calls all assigned saveHandlers
   */
  private async save(
    payload: ConfigObject,
    instance: Instance<Constraints>,
  ): Promise<void> {
    for await (const handler of this.saveHandlers) {
      await handler(instance.manifest.id, payload);
    }
  }

  /**
   * Calls all assigned getHandlers and returns response form the last one assigned.
   */
  private get(payload: never, instance: Instance<Constraints>): void {
    let config: ConfigObject | undefined = undefined;
    for (const handler of this.getHandlers) {
      config = handler(instance.manifest.id);
    }
    // to make it simple - update with a response from the latest handler
    instance.notify([ConfigApplet.Events.Update, config]);
  }

  /**
   * "State-dirty" action handler, calls all assigned stateDirtyHandlers.
   */
  private setDirtyState(): void {
    for (const handler of this.stateDirtyHandlers) {
      handler();
    }
  }

  /**
   * "State-reset" action handler, calls all assigned stateResetHandlers.
   */
  private resetState(): void {
    for (const handler of this.stateResetHandlers) {
      handler();
    }
  }
}
